import type { Activity, ActivityType, Application, ProductKind } from 'types';
import type { Unit } from 'core/types';
import type { Category } from '../category/types';
import type { Direction } from 'modules/campaign/row/smart';

/**
 * Values shared by the route-based Product form and type-scoped authoring.
 * Type-owned MEDIA configuration remains an opaque nullable object here.
 */
export type ProductFormValues = {
  kind: ProductKind | null;
  type: ActivityType | null;
  name: string;
  category: Category | null;
  activities: Activity[];
  unit: Unit | null;
  gross_price: string | null;
  direction: Direction | null;
  application: Application | null;
  configuration: Record<string, unknown> | null;
  disabled: boolean;
};

export function productFormInitialValues(type: ActivityType | null = null): ProductFormValues {
  return {
    kind: null,
    type,
    name: '',
    category: null,
    activities: [],
    unit: null,
    gross_price: null,
    direction: null,
    application: null,
    configuration: null,
    disabled: false,
  };
}

/**
 * Product representations accepted when building form values: any payload
 * carrying the serialized common fields. Missing fields fall back to defaults,
 * and `configuration` may be any typed configuration object, `null`, or absent.
 */
export type ProductFormValuesSource = Partial<Omit<ProductFormValues, 'type' | 'configuration'>> & {
  type: ActivityType | null;
  configuration?: unknown;
};

/**
 * Build the complete form shape from a Product representation. Keeping this
 * beside the initial-value factory makes route forms and authoring drawers
 * round-trip the same common fields instead of each filling in its own
 * defaults. A missing configuration in a list representation is treated as
 * the explicit unconfigured state; a present object is kept verbatim.
 */
export function productFormValuesFromProduct(product: ProductFormValuesSource): ProductFormValues {
  const { configuration, ...values } = product;
  return {
    ...productFormInitialValues(product.type),
    ...values,
    // A present configuration is a typed object per the serialization contract;
    // this is the single normalization boundary for that guarantee.
    configuration: configuration == null ? null : (configuration as Record<string, unknown>),
  };
}
